import AppButton from '@/components/Button.vue';
import { h, markRaw, type Component, type VNodeChild } from 'vue';
import { RouteLocationRaw } from 'vue-router';

export enum TableColumnType {
    Text = 'text',
    DateTime = 'datetime',
    Option = 'option',
    Boolean = 'boolean',
    Image = 'image',
    RouterLink = 'router-link',
    Html = 'html',
    Slot = 'slot',
    Component = 'component',
    Index = 'index',
    Actions = 'actions'
}

export type TableRow = Record<string, unknown>;
export type TableClass = string | string[] | Record<string, boolean>;
export type TableStyle = Record<string, string | number>;

export interface TableColumnBase<TRow extends Record<string, unknown> = TableRow> {
    label: string;
    tooltip?: string;
    headerClasses?: TableClass;
    headerStyles?: TableStyle;
    columnClasses?: TableClass;
    columnStyles?: TableStyle | ((row: TRow) => TableStyle);
}

export interface TableTextColumn<TRow extends Record<string, unknown> = TableRow> extends TableColumnBase<TRow> {
    type: TableColumnType.Text;
    key: keyof TRow & string;
}

export interface TableDateTimeColumn<TRow extends Record<string, unknown> = TableRow> extends TableColumnBase<TRow> {
    type: TableColumnType.DateTime;
    key: keyof TRow & string;
}

export interface TableOption {
    label: string;
    value: string | number | boolean | null;
    background?: string;
}

export interface TableOptionColumn<TRow extends Record<string, unknown> = TableRow> extends TableColumnBase<TRow> {
    type: TableColumnType.Option;
    key: keyof TRow & string;
    options: TableOption[];
}

export interface TableBooleanColumn<TRow extends Record<string, unknown> = TableRow> extends TableColumnBase<TRow> {
    type: TableColumnType.Boolean;
    key: keyof TRow & string;
}

export interface TableImageColumn<TRow extends Record<string, unknown> = TableRow> extends TableColumnBase<TRow> {
    type: TableColumnType.Image;
    imageKey: keyof TRow & string;
    statusKey?: keyof TRow & string;
}

export interface TableRouterLinkColumn<TRow extends Record<string, unknown> = TableRow> extends TableColumnBase<TRow> {
    type: TableColumnType.RouterLink;
    key: keyof TRow & string;
    to: (row: TRow) => RouteLocationRaw;
    content?: (row: TRow) => string;
}

export interface TableHtmlColumn<TRow extends Record<string, unknown> = TableRow> extends TableColumnBase<TRow> {
    type: TableColumnType.Html;
    htmlHandler: (row: TRow) => string;
}

export interface TableSlotColumn extends TableColumnBase<TableRow> {
    type: TableColumnType.Slot;
    slotName: string;
}

export type ComponentProps<TComponent extends Component> = TComponent extends new (
    ...args: infer _Args
) => infer Instance
    ? Instance extends { $props: infer Props }
        ? Props extends Record<string, unknown>
            ? Props
            : Record<string, unknown>
        : Record<string, unknown>
    : Record<string, unknown>;

export interface TableComponentColumn<
    TRow extends Record<string, unknown> = TableRow,
    TComponent extends Component = Component
> extends TableColumnBase<TRow> {
    type: TableColumnType.Component;
    component: TComponent;
    propsHandler?: (row: TRow) => ComponentProps<TComponent>;
    contentHandler?: (row: TRow) => VNodeChild;
}

export interface TableIndexColumn extends TableColumnBase<TableRow> {
    type: TableColumnType.Index;
}

export interface TableActionsColumn extends TableColumnBase<TableRow> {
    type: TableColumnType.Actions;
}

export type TableColumn<TRow extends Record<string, unknown> = TableRow> =
    | TableTextColumn<TRow>
    | TableDateTimeColumn<TRow>
    | TableOptionColumn<TRow>
    | TableBooleanColumn<TRow>
    | TableImageColumn<TRow>
    | TableRouterLinkColumn<TRow>
    | TableHtmlColumn<TRow>
    | TableSlotColumn
    | TableComponentColumn<TRow>
    | TableIndexColumn
    | TableActionsColumn;

export interface TableConfig<TRow extends Record<string, any> = TableRow> {
    columns: TableColumn<TRow>[];
    actions?: TableAction<TRow>[];
}
export interface LegacyTableColumn {
    /** @deprecated Use TableColumn instead */
    label: string;
    /** @deprecated Use TableTextColumn.key instead */
    field: string;
    /** @deprecated Use htmlHandler instead */
    display?: (row: Record<string, any>) => string;
    /** @deprecated Use a TableComponentColumn instead */
    component?: (row: any) => {
        type: Component;
        props?: any;
        message?: string;
    };
    tooltip?: string;
    headerClasses?: TableClass;
    headerStyles?: TableStyle;
    columnClasses?: TableClass;
    columnStyles?: TableStyle | ((row: Record<string, unknown>) => TableStyle);
}

/** @deprecated TODO: remove */
export type IColumn = LegacyTableColumn;
export type TableColumnDefinition<TRow extends Record<string, unknown> = TableRow> =
    | TableColumn<TRow>
    | LegacyTableColumn;

export type AppButtonProps = ComponentProps<typeof AppButton> & { onClick?: (event: MouseEvent) => void };
export interface TableAction<TRow extends Record<string, unknown> = any> {
    props: (row: any) => AppButtonProps & {
        /** @deprecated Use `canShow` instead */
        isShow?: boolean;
    };
    group?: string | number;
    canShow?: (row: TRow) => boolean;
}

/** @deprecated TODO: remove */
export type IAction<TRow extends Record<string, unknown> = any> = TableAction<TRow>;

const ACTIONS_LABEL = 'Actions';
const INDEX_FIELD = '#';

export const normalizeTableColumns = (
    columns: TableColumnDefinition[],
    isSlotMode: boolean,
    hasSlot: (slotName: string) => boolean
): TableColumn[] =>
    columns.map(column => {
        if ('type' in column) {
            return column;
        }

        const appearance = {
            label: column.label,
            tooltip: column.tooltip,
            headerClasses: column.headerClasses,
            headerStyles: column.headerStyles,
            columnClasses: column.columnClasses,
            columnStyles: column.columnStyles
        };

        if (column.field === INDEX_FIELD) {
            return {
                ...appearance,
                type: TableColumnType.Index
            };
        }

        if (column.label === ACTIONS_LABEL) {
            return {
                ...appearance,
                type: TableColumnType.Actions
            };
        }

        if (column.display) {
            return {
                ...appearance,
                type: TableColumnType.Html,
                htmlHandler: column.display
            };
        }

        if (column.component) {
            const legacyComponent = column.component;

            return {
                ...appearance,
                type: TableColumnType.Component,
                component: markRaw((props: { row: Record<string, unknown> }) => {
                    const rendered = legacyComponent(props.row);
                    return h(rendered.type, rendered.props, { default: () => rendered.message });
                }),
                propsHandler: row => ({ row })
            };
        }

        if (isSlotMode && hasSlot(column.field)) {
            return {
                ...appearance,
                type: TableColumnType.Slot,
                slotName: column.field
            };
        }

        return {
            ...appearance,
            type: TableColumnType.Text,
            key: column.field
        };
    });

const ACTION_SIZE = 'xs';
const FIRST_ACTION_CLASS = 'ml-1';
const CONTINUOUS_ACTION_CLASS = 'ml-0';

export function resolveTableActions(actionList: TableAction[], row: TableRow): Array<Record<string, unknown>> {
    const groups = new Map<string | number, TableAction[]>();

    actionList.forEach(action => {
        const groupKey = action.group ?? 0;
        const group = groups.get(groupKey) ?? [];

        group.push(action);
        groups.set(groupKey, group);
    });

    const resolvedActions: Array<Record<string, unknown>> = [];
    let previousGroup: string | number | null = null;

    groups.forEach((group, groupKey) => {
        group.forEach(action => {
            const { isShow, ...props } = action.props(row);
            const invisible = isShow === false || action.canShow?.(row) === false;
            if (invisible) {
                return;
            }

            resolvedActions.push({
                ...props,
                size: ACTION_SIZE,
                class: previousGroup === groupKey ? CONTINUOUS_ACTION_CLASS : FIRST_ACTION_CLASS
            });

            previousGroup = groupKey;
        });
    });

    return resolvedActions;
}

export const componentColumn =
    <TRow extends Record<string, unknown>>() =>
    <TComponent extends Component>(
        column: Omit<TableComponentColumn<TRow, TComponent>, 'type'>
    ): TableComponentColumn<TRow, TComponent> => ({
        type: TableColumnType.Component,
        ...column
    });
